import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import "dayjs/locale/sk";
import ReservationTableItem from './ReservationTableItem.jsx';
import {OVERVIEW_FILTER_ROOM_TYPES} from '../../../../utils/constants/enums.js';
import {Spinner} from '../../index.jsx';
import { useTranslation } from 'react-i18next';

// Extend dayjs with comparison plugins used in this file
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const ReservationsOverviewTable = ({
                                     reservations,
                                     selectedDate,
                                     rooms,
                                     isLoadingReservations,
                                     showOnlyRooms,
                                     isCms = false,
                                   }) => {
  const user = useSelector(reduxStore => reduxStore.auth.user);
  const { t } = useTranslation();
  const tableRef = useRef(null);

  // Zaregistrujeme vlastný locale
  dayjs.locale({
    ...dayjs.Ls["sk"], // zdedíme všetko zo SK
    weekdaysMin: ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"], // prepíšeme len to, čo chceme
  }, null, true);

  const days = useMemo(() => {
    const totalDays = selectedDate.daysInMonth();

    let daysArr = [];
    for (let i = 1; i <= totalDays; ++i) {
      daysArr.push(new Date(selectedDate.year(), selectedDate.month(), i));
    }

    return daysArr;
  }, [selectedDate]);

  const workingDays = useMemo(() => {
    return days.filter((day) => ![0, 6].includes(dayjs(day).day()));
  }, [days]);

  // Filter rooms based on selected filters
  const filteredRooms = useMemo(() => {
    return rooms.filter(room => {
      return showOnlyRooms.some(type => {
        if (type === OVERVIEW_FILTER_ROOM_TYPES.DEPARTMENT_ROOM) {
          return room.department_room === 'Yes';
        }
        if ((type === OVERVIEW_FILTER_ROOM_TYPES.RA || type === OVERVIEW_FILTER_ROOM_TYPES.RB || type === OVERVIEW_FILTER_ROOM_TYPES.RC) &&
          (room.department_room !== 'Yes')) {
          return room.title.includes(type);
        }
        if (type === OVERVIEW_FILTER_ROOM_TYPES.OTHER && (room.department_room !== 'Yes')) {
          return !room.title.includes(OVERVIEW_FILTER_ROOM_TYPES.RA)
            && !room.title.includes(OVERVIEW_FILTER_ROOM_TYPES.RB)
            && !room.title.includes(OVERVIEW_FILTER_ROOM_TYPES.RC);
        }
        return false;
      });
    });
  }, [rooms, showOnlyRooms]);

  const [activeCell, setActiveCell] = useState({ row: 0, col: 0 });

  useEffect(() => {
    if (workingDays.length === 0 || filteredRooms.length === 0) {
      return;
    }

    setActiveCell((currentCell) => ({
      row: Math.min(currentCell.row, workingDays.length - 1),
      col: Math.min(currentCell.col, filteredRooms.length - 1),
    }));
  }, [workingDays.length, filteredRooms.length]);

  const getReservationsForDay = (room_id, day) => {
    const dayStart = dayjs(day).startOf('day');
    const dayEnd = dayjs(day).endOf('day');

    return reservations.filter(reservation => {
      if (Number(reservation.room_id) !== Number(room_id)) {
        return false;
      }

      const from = dayjs(reservation.date_from);
      const to = dayjs(reservation.date_to);

      return from.isSameOrBefore(dayEnd) && to.isSameOrAfter(dayStart);
    });
  };

  const focusCell = useCallback((row, col) => {
    const cell = tableRef.current?.querySelector(
      `[data-row-index="${row}"][data-col-index="${col}"]`,
    );

    if (cell) {
      cell.focus();
    }
  }, []);

  const handleCellKeyDown = useCallback(
    (event, rowIndex, colIndex) => {
      let nextRow = rowIndex;
      let nextCol = colIndex;

      if (event.key === 'ArrowUp') {
        nextRow = Math.max(0, rowIndex - 1);
      } else if (event.key === 'ArrowDown') {
        nextRow = Math.min(workingDays.length - 1, rowIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        nextCol = Math.max(0, colIndex - 1);
      } else if (event.key === 'ArrowRight') {
        nextCol = Math.min(filteredRooms.length - 1, colIndex + 1);
      } else if (event.key === 'Home') {
        nextCol = 0;
      } else if (event.key === 'End') {
        nextCol = filteredRooms.length - 1;
      } else {
        return;
      }

      event.preventDefault();

      if (nextRow !== rowIndex || nextCol !== colIndex) {
        focusCell(nextRow, nextCol);
      }
    },
    [filteredRooms.length, focusCell, workingDays.length],
  );

  const handleCellFocus = useCallback((rowIndex, colIndex) => {
    setActiveCell({ row: rowIndex, col: colIndex });
  }, []);

  if (isLoadingReservations) {
    return <Spinner>
    </Spinner>
  }

  return (
    <>
      <div className="overview-table">
        <table
          ref={tableRef}
          role="grid"
          aria-label={t('Prehľad rezervácií miestností')}
        >
          <thead>
          <tr>
            <th>{t('Dátum')}</th>
            {filteredRooms.map(room => (
              <th style={{backgroundColor: room.department_room === 'Yes' ? '#ffe066' : null}} key={room.id}>
                <span style={{whiteSpace: 'nowrap'}}>{room.title}</span><br/><span style={{fontWeight: 'normal'}}>{room.department_code}</span>
              </th>
            ))}
          </tr>
          </thead>
          <tbody>
            {workingDays.map((day, dayIndex) => {
              const isPastDay = dayjs(day).isBefore(dayjs(), 'day');
              return (
                <tr
                  key={dayjs(day).format('YYYY-MM-DD')}
                  className={isPastDay ? 'disabled' : 'active'}
                >
                  <td style={{ fontWeight: 'bold' }}>
                    {dayjs(day).format('D.M. (dd)')}
                  </td>
                  {filteredRooms.map((room, roomIndex) => {
                    const reservationDataForCell = getReservationsForDay(room.id, day);

                    return (
                      <ReservationTableItem
                        key={`room-${room.id}-day-${dayjs(day).format('YYYY-MM-DD')}`}
                        itemId={`room-${room.id}-day-${dayjs(day).format('YYYY-MM-DD')}`}
                        reservationData={reservationDataForCell}
                        popoverTarget={{
                          room: room.id,
                          day: dayjs(day).format('YYYY-MM-DD'),
                        }}
                        user={user}
                        isCms={isCms}
                        className="resdate-item"
                        isDisabled={isPastDay}
                        rowIndex={dayIndex}
                        colIndex={roomIndex}
                        tabIndex={
                          activeCell.row === dayIndex && activeCell.col === roomIndex
                            ? 0
                            : -1
                        }
                        onCellFocus={handleCellFocus}
                        onCellKeyDown={handleCellKeyDown}
                        ariaLabel={t(
                          '{{date}} - {{room}} - {{count}} rezervacii',
                          {
                            date: dayjs(day).format('D.M.YYYY'),
                            room: room.title,
                            count: reservationDataForCell.length,
                          },
                        )}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ReservationsOverviewTable;
